import React, { useState, useRef, useEffect } from "react";
import "./timePickerDropdown.css";
import { convertTo12Hour } from "./meetingUtils";

const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const timeStr = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
      let displayHour = hour;
      if (hour === 0) displayHour = 12;
      else if (hour > 12) displayHour = hour - 12;
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayTime = `${displayHour}:${String(minute).padStart(2, "0")}${ampm}`;
      slots.push({ value: timeStr, label: displayTime });
    }
  }
  return slots;
};

const TIME_SLOTS = generateTimeSlots();


const TimePickerDropdown = ({ value, onChange, hasError, disabled, onError }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [filteredSlots, setFilteredSlots] = useState(TIME_SLOTS);
  const [formatError, setFormatError] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const escapedRef = useRef(false);

  useEffect(() => {
    if (value) {
      const slot = TIME_SLOTS.find((s) => s.value === value);
      setInputValue(slot?.label || convertTo12Hour(value));
      setFormatError(false);
    } else {
      setInputValue("");
      setFormatError(false);
    }
  }, [value]);

  const parseTimeInput = (input) => {
    if (!input?.trim()) return null;

    // Strip dots so "p.m." normalizes to "pm"
    const raw = input.trim().toLowerCase().replace(/\./g, "");

    // Only digits, colon, whitespace and the letters a/p/m
    if (/[^0-9:\sapm]/.test(raw)) return null;

    // Meridiem must be trailing: "9p", "9pm", "9 pm"
    const meridiemMatch = raw.match(/\s*([ap])m?\s*$/);
    const meridiem = meridiemMatch ? meridiemMatch[1] : null;
    const digits = (meridiemMatch ? raw.slice(0, meridiemMatch.index) : raw).trim();

    // Reject stray letters outside the meridiem ("9b", "am" alone)
    if (!digits || /[a-z]/.test(digits)) return null;

    let hour;
    let minute;

    if (digits.includes(":")) {
      const m = digits.match(/^(\d{1,2}):(\d{1,2})$/);
      if (!m) return null; // rejects "9:", ":30", "9:30:00"
      hour = Number(m[1]);
      minute = Number(m[2]);
    } else {
      if (!/^\d{1,4}$/.test(digits)) return null;
      if (digits.length <= 2) {
        hour = Number(digits);
        minute = 0;
      } else {
        // 3-4 digits are always HMM / HHMM
        hour = Number(digits.slice(0, -2));
        minute = Number(digits.slice(-2));
      }
    }

    if (minute > 59) return null;

    if (meridiem) {
      if (hour < 1 || hour > 12) return null; // "13pm" is nonsense
      if (meridiem === "p" && hour !== 12) hour += 12;
      if (meridiem === "a" && hour === 12) hour = 0;
    } else if (hour > 23) {
      return null; // no meridiem → 24-hour clock
    }

    return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  };

  const handleInputChange = (e) => {
    let text = e.target.value;

    // Only allow digits, colon, space, and letters a, p, m (for am/pm)
    text = text.replace(/[^0-9:\sapm]/gi, "");

    // Limit reasonable length (max ~10 chars for "1234 pm")
    if (text.length > 10) {
      text = text.slice(0, 10);
    }

    setInputValue(text);

    if (text.trim() === "") {
      setFilteredSlots(TIME_SLOTS);
    } else {
      // Normalize for matching: strip spaces and colons
      const norm = (s) => s.toLowerCase().replace(/[\s:]/g, "");
      const q = norm(text);
      const filtered = TIME_SLOTS.filter((slot) =>
        norm(slot.label).includes(q) || norm(slot.value).includes(q)
      );
      setFilteredSlots(filtered);
    }
    setIsOpen(true);
  };

  const handleSlotSelect = (slot) => {
    setInputValue(slot.label);
    setFormatError(false);
    onError?.(false); // Clear parent's error state
    onChange(slot.value);
    setIsOpen(false);
  };

  const handleInputBlur = () => {
    if (escapedRef.current) {
      escapedRef.current = false;
      return; // Escape already handled the revert
    }

    setIsOpen(false);

    if (inputValue.trim() === "") {
      // Blank input is an error
      setFormatError(true);
      onError?.(true);
      return;
    }

    // Try to parse custom input
    const parsed = parseTimeInput(inputValue);
    if (parsed) {
      setFormatError(false);
      onError?.(false);
      onChange(parsed);
    } else {
      // Invalid format
      setFormatError(true);
      onError?.(true);
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      inputRef.current?.blur();
    } else if (e.key === "Escape") {
      e.preventDefault();
      escapedRef.current = true;
      // Restore the last committed value
      const slot = TIME_SLOTS.find((s) => s.value === value);
      setInputValue(value ? slot?.label || convertTo12Hour(value) : "");
      setFormatError(false);
      onError?.(false);
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    // Reset filtered slots to show all times
    setFilteredSlots(TIME_SLOTS);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="time-picker-wrapper">
      <div
        className={`time-picker-input-group ${hasError || formatError ? "time-picker-error" : ""}`}
        ref={dropdownRef}
      >
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onClick={() => setIsOpen(true)}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          placeholder="Select time"
          disabled={disabled}
          className="time-picker-input"
        />
        <span className="time-picker-icon">
          <i className="fa-regular fa-clock"></i>
        </span>
        <span className="down-arrow">
          <i className="fa-solid fa-chevron-down"></i>

        </span>

        {isOpen && filteredSlots.length > 0 && (
          <div className="time-picker-dropdown">
            <div className="time-picker-scroll">
              {filteredSlots.map((slot) => (
                <div
                  key={slot.value}
                  className={`time-picker-option ${
                    value === slot.value ? "time-picker-option-active" : ""
                  }`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSlotSelect(slot);
                  }}
                >
                  {slot.label}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimePickerDropdown;
